/* eslint-disable react/jsx-no-bind */

import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import cn from "classnames";
import Icon from "~/components/shared/Icon";
import Badge from "~/components/shared/Badge";
import FriendlyTime from "~/components/shared/FriendlyTime";
import { getLanguageColor } from "~/components/pipeline/New/ConnectGitPicker/util/languageColors";
import type {
  SCMServiceInfo,
  RepositoryInfo,
  ManualProviderType,
} from "~/components/pipeline/New/ConnectGitPicker/util/types";
import Combox from "~/components/shared/Combox";
import ComboxTrigger from "~/components/shared/Combox/ComboxTrigger";
import ComboxPopover from "~/components/shared/Combox/ComboxPopover";
import ComboxList from "~/components/shared/Combox/ComboxList";
import ComboxItem from "~/components/shared/Combox/ComboxItem";
import ComboxFilterInput from "~/components/shared/Combox/ComboxFilterInput";
import Spinner from "~/components/shared/Spinner";
import exampleToRepo, {
  Example,
} from "~/components/pipeline/New/ConnectGitPicker/util/exampleToRepo";

// Global cache for repositories by service UUID
const repositoryCache = new Map<string, RepositoryInfo[]>();

interface RepositoryPickerProps {
  selectedService: SCMServiceInfo | null;
  selectedRepository: RepositoryInfo | null;
  onRepositorySelect: (repository: RepositoryInfo | null) => void;
  onAutoSelectRepository?: (
    repositories: RepositoryInfo[],
    serviceUuid: string,
    force?: boolean,
  ) => void;

  repositoryValue?: string | null;
  onRepositoryUrlChange?: (url: string) => void;
  selectedManualProvider?: ManualProviderType;
}

const RepositoryPickerComponent = ({
  selectedService,
  selectedRepository,
  onRepositorySelect,
  onAutoSelectRepository,
  repositoryValue,
  onRepositoryUrlChange,
  selectedManualProvider = "git",
}: RepositoryPickerProps) => {
  const [manualUrl, setManualUrl] = useState(repositoryValue || "");
  const [repositories, setRepositories] = useState<RepositoryInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const lastAutoSelectedServiceRef = useRef<string | null>(null);

  const fetchAllRepositories = useCallback(() => {
    if (!selectedService?.repositories_url) {
      setRepositories([]);
      setLoading(false);
      setError(false);
      return;
    }

    // Check cache first
    const cachedRepos = repositoryCache.get(selectedService.uuid);
    if (cachedRepos) {
      setRepositories(cachedRepos);
      setLoading(false);
      setError(false);
      return;
    }

    // Fetch ALL repositories for the service (no search parameter)
    const url = selectedService.repositories_url;

    setLoading(true);
    setError(false);

    fetch(url, {
      headers: { "X-Buildkite-Frontend-Version": BUILDKITE_FRONTEND_VERSION },
    })
      .then((response) => response.json())
      .then((repoResponse: RepositoryInfo[] | Example[]) => {
        const repositories =
          selectedService.uuid === "examples"
            ? exampleToRepo(repoResponse as Example[])
            : (repoResponse as RepositoryInfo[]);

        // Only update if this is still the current service
        if (selectedService?.repositories_url === url) {
          // Cache the repositories
          repositoryCache.set(selectedService.uuid, repositories);
          setRepositories(repositories);
          setLoading(false);

          // Auto-select first repository if we have repositories and no selection
          if (repositories.length > 0 && !selectedRepository && onAutoSelectRepository) {
            const isNewService = lastAutoSelectedServiceRef.current !== selectedService.uuid;
            if (isNewService) {
              onAutoSelectRepository(repositories, selectedService.uuid, true);
              lastAutoSelectedServiceRef.current = selectedService.uuid;
            }
          }
        }
      })
      .catch(() => {
        // Only update error state if this is still the current service
        if (selectedService?.repositories_url === url) {
          setRepositories([]);
          setLoading(false);
          setError(true);
        }
      });
  }, [
    selectedService?.repositories_url,
    selectedService?.uuid,
    selectedRepository,
    onAutoSelectRepository,
  ]);

  // Initialize repositories from pre-loaded data, cache, or fetch when service changes
  useEffect(() => {
    if (selectedService) {
      // Priority order: 1) Pre-loaded data, 2) Cache, 3) Fetch
      if (selectedService.repositories) {
        // Instant loading using pre-loaded repositories
        setRepositories(selectedService.repositories);
        // Also update cache with preloaded data
        repositoryCache.set(selectedService.uuid, selectedService.repositories);
        setLoading(false);
        setError(false);

        // Auto-select first repository if we have repositories and no selection
        if (
          selectedService.repositories.length > 0 &&
          !selectedRepository &&
          onAutoSelectRepository
        ) {
          const isNewService = lastAutoSelectedServiceRef.current !== selectedService.uuid;
          if (isNewService) {
            onAutoSelectRepository(selectedService.repositories, selectedService.uuid, true);
            lastAutoSelectedServiceRef.current = selectedService.uuid;
          }
        }
      } else {
        // Check cache before fetching
        const cachedRepos = repositoryCache.get(selectedService.uuid);
        if (cachedRepos) {
          setRepositories(cachedRepos);
          setLoading(false);
          setError(false);

          // Auto-select if needed
          if (cachedRepos.length > 0 && !selectedRepository && onAutoSelectRepository) {
            const isNewService = lastAutoSelectedServiceRef.current !== selectedService.uuid;
            if (isNewService) {
              onAutoSelectRepository(cachedRepos, selectedService.uuid, true);
              lastAutoSelectedServiceRef.current = selectedService.uuid;
            }
          }
        } else {
          // Fetch ALL repositories for this service once
          fetchAllRepositories();
        }
      }
    } else {
      setRepositories([]);
      setLoading(false);
      setError(false);
      lastAutoSelectedServiceRef.current = null;
    }
  }, [selectedService, fetchAllRepositories, selectedRepository, onAutoSelectRepository]);

  // Reset state when service changes
  useEffect(() => {
    lastAutoSelectedServiceRef.current = null;
  }, [selectedService?.uuid]);

  // Update manual URL when repositoryValue changes
  useEffect(() => {
    if (repositoryValue !== undefined) {
      setManualUrl(repositoryValue || "");
    }
  }, [repositoryValue]);

  // Memoized manual URL handlers
  const handleManualUrlChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setManualUrl(event.target.value);
  }, []);

  const handleManualUrlBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onRepositoryUrlChange?.(event.target.value);
    },
    [onRepositoryUrlChange],
  );

  // Memoized placeholder text
  const placeholderText = useMemo(() => {
    switch (selectedManualProvider) {
      case "gitlab":
        return "git@gitlab.com:your/repo.git";
      case "bitbucket":
        return "git@bitbucket.org:your/repo.git";
      default:
        return "git@github.com:your/repo.git";
    }
  }, [selectedManualProvider]);

  // Memoized button text
  const buttonText = useMemo(() => {
    if (selectedRepository) {
      return selectedRepository.full_name.split("/").pop() || selectedRepository.full_name;
    }
    // Show loading only when actually fetching, not when switching
    if (selectedService && !selectedRepository) {
      // If we have repositories but no selection, show prompt to select
      if (repositories.length > 0) {
        return "Select a repository...";
      }
      // Only show loading if we're actually loading AND have no repositories
      if (loading && repositories.length === 0) {
        return error ? "Failed to load repositories" : <Spinner color={false} />;
      }
      // If no repositories and not loading
      if (repositories.length === 0 && !loading) {
        return error ? "Failed to load repositories" : "No repositories found";
      }
    }
    if (repositoryValue?.trim()) {
      // Extract repo name from URL
      try {
        const sshMatch = repositoryValue.match(/^[^@]+@[^:]+:([^#]+?)(?:\.git)?$/);
        if (sshMatch?.[1]) {
          return sshMatch[1];
        }

        const httpsMatch = repositoryValue.match(/^https?:\/\/[^/]+\/(.+?)(?:\.git)?(?:#.*)?$/);
        if (httpsMatch?.[1]) {
          return httpsMatch[1];
        }
      } catch (error) {
        // Ignore parsing errors
      }
      return repositoryValue;
    }
    // Fallback to ensure we always show something
    const result = "Select repository";
    return result;
  }, [selectedRepository, selectedService, repositories.length, loading, error, repositoryValue]);

  // Memoized repository icon function
  const getRepositoryIcon = useCallback((visibility: string) => {
    switch (visibility) {
      case "private":
        return "repo-private";
      case "internal":
        return "repo-internal";
      case "public":
        return "repo-public";
      default:
        return "repo-public";
    }
  }, []);

  // Empty state component
  const emptyStateContent = useMemo(() => {
    if (loading && repositories.length === 0) {
      return "Loading repositories...";
    }

    if (error) {
      return "Failed to load repositories. Please try again.";
    }

    if (repositories.length === 0 && selectedService) {
      return `No repositories found for ${selectedService.account_login}.`;
    }

    return "No repositories found";
  }, [loading, error, repositories.length, selectedService]);

  // Manual URL input for non-connected services
  if (!selectedService) {
    return (
      <div className="min-w-0 flex-auto">
        <div className="relative">
          <input
            type="text"
            className="input -my-px -ml-px h-[32px] pr-10"
            placeholder={placeholderText}
            name="project[repository]"
            id="project_repository"
            autoComplete="off"
            value={manualUrl}
            onChange={handleManualUrlChange}
            onBlur={handleManualUrlBlur}
          />
        </div>
      </div>
    );
  }

  return (
    <Combox
      value={selectedRepository?.html_url || ""}
      onValueChange={(value) => {
        const repository = repositories.find((repo) => repo.html_url === value);
        if (repository) {
          onRepositorySelect(repository);
          setComboboxOpen(false);
        }
      }}
      open={comboboxOpen}
      onOpenChange={setComboboxOpen}
      disabled={!selectedService && !selectedRepository && !repositoryValue?.trim()}
    >
      <ComboxTrigger
        disabled={!selectedService && !selectedRepository && !repositoryValue?.trim()}
        className="w-full"
        data-testid="git-repository-picker-trigger"
        id="repository-select"
      >
        <div className="flex items-center gap-2">
          {selectedRepository && (
            <Icon
              icon={getRepositoryIcon(selectedRepository.visibility)}
              className="h-4 w-4 text-gray-800"
            />
          )}
          <span className="truncate">{buttonText}</span>
        </div>
      </ComboxTrigger>

      <ComboxPopover data-testid="git-repository-picker-content">
        <ComboxFilterInput />
        <ComboxList emptyState={emptyStateContent}>
          {repositories.map((repository) => (
            <ComboxItem value={repository.html_url} key={repository.html_url}>
              <div className="flex w-full min-w-0 flex-row items-center gap-2 rounded pl-1 pr-2">
                <span title={repository.visibility + " repository"}>
                  <Icon
                    icon={getRepositoryIcon(repository.visibility)}
                    className="h-4 w-4 text-gray-800"
                  />
                </span>

                <div className="flex min-w-0 flex-1 flex-col justify-start gap-0 text-left">
                  <div className="flex w-full flex-row gap-1">
                    <h3 className="m-0 truncate p-0 text-sm font-medium">
                      {repository.full_name.split("/").pop()}
                    </h3>
                    {repository.fork && (
                      <Badge outline={true} className="flex items-center py-0 text-xs">
                        Fork
                      </Badge>
                    )}
                  </div>

                  {repository.description ? (
                    <p
                      className={cn([
                        "dark-gray m-0 truncate p-0 text-xs",
                        selectedRepository?.html_url === repository.html_url &&
                          "!text-purple-600/70",
                      ])}
                    >
                      {repository.description}
                    </p>
                  ) : (
                    <p
                      className={cn([
                        "dark-gray m-0 truncate p-0 text-xs italic text-gray-600",
                        selectedRepository?.html_url === repository.html_url &&
                          "!text-purple-600/70",
                      ])}
                    >
                      No description
                    </p>
                  )}
                </div>

                <div className="ml-auto flex w-fit max-w-32 flex-col items-end gap-0 text-right">
                  {repository.updated_at && (
                    <FriendlyTime
                      value={repository.updated_at}
                      format="relative"
                      className="dark-gray m-0 truncate p-0 text-right text-xs"
                      clickable={false}
                      toggleable={false}
                    />
                  )}
                  {repository.language ? (
                    <div className="flex items-center gap-1">
                      <div
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: getLanguageColor(repository.language) }}
                      />
                      <p className="dark-gray m-0 truncate p-0 text-right text-xs">
                        {repository.language}
                      </p>
                    </div>
                  ) : (
                    <p className="m-0 truncate p-0 text-right text-xs italic text-gray-600">
                      Not detected
                    </p>
                  )}
                </div>
              </div>
            </ComboxItem>
          ))}
        </ComboxList>
      </ComboxPopover>
    </Combox>
  );
};

export const RepositoryPicker = memo(RepositoryPickerComponent);
